import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Button, Spinner, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type TemplateDownloadProps = {
    isPreparing: boolean;
    hasFailed: boolean;
    onDownload: () => void;
};

/**
 * The Download template button, its note that the headers follow the UI
 * language, and the alert a failed build leaves. The button keeps its label
 * while the workbook builds; pressing it again after a failure is the retry.
 */
export const TemplateDownload: React.FC<TemplateDownloadProps> = ({
    isPreparing,
    hasFailed,
    onDownload,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                <Button
                    isPending={isPreparing}
                    onPress={onDownload}
                    size="sm"
                    variant="secondary"
                    {...getNodeTestIdProp('catalog-import-template-download')}
                >
                    {({ isPending }) => (
                        <>
                            {isPending ? (
                                <Spinner color="current" size="sm" />
                            ) : (
                                <Icon
                                    aria-hidden
                                    icon="gravity-ui:file-arrow-down"
                                />
                            )}
                            {t('catalogImport.upload.template.download')}
                        </>
                    )}
                </Button>
                <Typography color="muted" type="body-xs">
                    {t('catalogImport.upload.template.languageNote')}
                </Typography>
            </div>
            {hasFailed ? (
                <Alert
                    status="danger"
                    {...getNodeTestIdProp('catalog-import-template-error')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.upload.template.failed')}
                        </Alert.Title>
                        <Alert.Description>
                            {t('catalogImport.upload.template.failedHint')}
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            ) : null}
        </div>
    );
};
